import { useState, useRef, useEffect } from 'react';
import { useStoreSettings, COLOR_PRESETS, DAY_ORDER, DAY_LABELS, type BusinessHours } from '@/hooks/useStoreSettings';

interface StoreSettingsProps {
  onToast: (msg: string) => void;
}

type SectionSaveState = 'idle' | 'saving' | 'saved' | 'error';

function useSectionSave(onToast: (msg: string) => void) {
  const [state, setState] = useState<SectionSaveState>('idle');

  const save = async (fn: () => void | Promise<void>, successMsg: string) => {
    setState('saving');
    try {
      await fn();
      setState('saved');
      onToast(successMsg);
      setTimeout(() => setState('idle'), 2500);
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 2500);
    }
  };

  return { state, save };
}

function SaveButton({ state, onClick, label = 'حفظ' }: { state: SectionSaveState; onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      disabled={state === 'saving'}
      className={`mt-4 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
        state === 'saved'
          ? 'bg-green-500 text-white'
          : state === 'error'
          ? 'bg-red-500 text-white'
          : 'bg-[#1a1a1a] hover:bg-[#333] text-white'
      }`}
    >
      {state === 'saving' ? (
        <i className="ri-loader-4-line animate-spin"></i>
      ) : state === 'saved' ? (
        <><i className="ri-checkbox-circle-line"></i> تم الحفظ!</>
      ) : state === 'error' ? (
        <><i className="ri-error-warning-line"></i> خطأ في الحفظ</>
      ) : (
        <><i className="ri-save-line"></i> {label}</>
      )}
    </button>
  );
}

export default function StoreSettings({ onToast }: StoreSettingsProps) {
  const { settings, updateSettings, resetSettings } = useStoreSettings();
  const [form, setForm] = useState({ ...settings });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoPreview, setLogoPreview] = useState<string>(settings.logoUrl || '');
  const [selectedColorId, setSelectedColorId] = useState<string>(settings.colorPresetId || 'gold');
  const [logoTab, setLogoTab] = useState<'image' | 'text'>(settings.logoUrl ? 'image' : 'text');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // CallMeBot state
  const [callMeBotKey, setCallMeBotKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Per-section save states
  const storeNameSave = useSectionSave(onToast);
  const logoSave = useSectionSave(onToast);
  const colorSave = useSectionSave(onToast);
  const whatsappSave = useSectionSave(onToast);
  const paymentSave = useSectionSave(onToast);
  const announcementSave = useSectionSave(onToast);
  const hoursSave = useSectionSave(onToast);
  const callmebotSave = useSectionSave(onToast);

  useEffect(() => {
    const saved = localStorage.getItem('whatsapp_settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.callMeBotApiKey) setCallMeBotKey(parsed.callMeBotApiKey);
      } catch { /* ignore */ }
    }
  }, []);

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrors(er => ({ ...er, logo: 'حجم الصورة يجب أن يكون أقل من 2MB' }));
      return;
    }
    setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setLogoPreview(dataUrl);
      setForm(f => ({ ...f, logoUrl: dataUrl }));
      setErrors(er => ({ ...er, logo: '' }));
      setLogoUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview('');
    setForm(f => ({ ...f, logoUrl: '' }));
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const updateDayHours = (day: keyof BusinessHours, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setForm(f => ({
      ...f,
      businessHours: {
        ...f.businessHours,
        [day]: { ...f.businessHours[day], [field]: value },
      },
    }));
  };

  const formatTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const period = h < 12 ? 'ص' : 'م';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  // ── Section Save Handlers ──

  const handleSaveStoreName = () => {
    if (!form.storeName.trim()) {
      setErrors(er => ({ ...er, storeName: 'يرجى إدخال اسم المتجر' }));
      return;
    }
    setErrors(er => ({ ...er, storeName: '' }));
    storeNameSave.save(() => updateSettings({ storeName: form.storeName.trim() }), 'تم حفظ اسم المتجر ✓');
  };

  const handleSaveLogo = () => {
    logoSave.save(() => updateSettings({ logoUrl: form.logoUrl, logoText: form.logoText, logoSubText: form.logoSubText }), 'تم حفظ شعار المتجر ✓');
  };

  const handleSaveColor = () => {
    colorSave.save(() => updateSettings({ colorPresetId: selectedColorId }), 'تم حفظ لون المتجر ✓');
  };

  const handleSaveWhatsApp = () => {
    if (!form.adminWhatsApp.trim()) {
      setErrors(er => ({ ...er, adminWhatsApp: 'يرجى إدخال رقم الواتساب' }));
      return;
    }
    if (!/^\d{10,15}$/.test(form.adminWhatsApp.replace(/\s/g, ''))) {
      setErrors(er => ({ ...er, adminWhatsApp: 'رقم الواتساب يجب أن يكون أرقاماً فقط (10-15 رقم)' }));
      return;
    }
    setErrors(er => ({ ...er, adminWhatsApp: '' }));
    whatsappSave.save(() => updateSettings({ adminWhatsApp: form.adminWhatsApp }), 'تم حفظ رقم الواتساب ✓');
  };

  const handleSavePayment = () => {
    if (!form.paymentCashEnabled && !form.paymentBankEnabled) {
      setErrors(er => ({ ...er, payment: 'يجب تفعيل طريقة دفع واحدة على الأقل' }));
      return;
    }
    if (form.paymentBankEnabled) {
      const errs: Record<string, string> = {};
      if (!form.bankName.trim()) errs.bankName = 'يرجى إدخال اسم البنك';
      if (!form.bankAccountName.trim()) errs.bankAccountName = 'يرجى إدخال اسم صاحب الحساب';
      if (!form.bankAccountNumber.trim()) errs.bankAccountNumber = 'يرجى إدخال رقم الحساب';
      if (!form.bankIban.trim()) errs.bankIban = 'يرجى إدخال رقم الآيبان';
      if (Object.keys(errs).length > 0) { setErrors(er => ({ ...er, ...errs })); return; }
    }
    setErrors(er => ({ ...er, payment: '', bankName: '', bankAccountName: '', bankAccountNumber: '', bankIban: '' }));
    paymentSave.save(() => updateSettings({
      paymentCashEnabled: form.paymentCashEnabled,
      paymentBankEnabled: form.paymentBankEnabled,
      bankName: form.bankName,
      bankAccountName: form.bankAccountName,
      bankAccountNumber: form.bankAccountNumber,
      bankIban: form.bankIban,
    }), 'تم حفظ طرق الدفع ✓');
  };

  const handleSaveAnnouncement = () => {
    announcementSave.save(() => updateSettings({ announcementText: form.announcementText, announcementEnabled: form.announcementEnabled }), 'تم حفظ الشريط الإعلاني ✓');
  };

  const handleSaveHours = () => {
    hoursSave.save(() => updateSettings({ businessHours: form.businessHours }), 'تم حفظ ساعات العمل ✓');
  };

  const handleSaveCallMeBot = () => {
    callmebotSave.save(() => {
      const existing = localStorage.getItem('whatsapp_settings');
      const parsed = existing ? JSON.parse(existing) : {};
      parsed.callMeBotApiKey = callMeBotKey.trim();
      localStorage.setItem('whatsapp_settings', JSON.stringify(parsed));
    }, callMeBotKey.trim() ? 'تم حفظ CallMeBot API Key ✓' : 'تم مسح الـ API Key');
  };

  const handleReset = () => {
    resetSettings();
    setForm({
      storeName: 'متجر الإبداع',
      adminWhatsApp: '966541138847',
      announcementText: 'شحن مجاني لجميع الطلبات داخل المملكة',
      announcementEnabled: true,
      paymentCashEnabled: true,
      paymentBankEnabled: true,
      bankName: '',
      bankAccountName: '',
      bankAccountNumber: '',
      bankIban: '',
      logoUrl: '',
      logoText: 'إبداع',
      logoSubText: 'AL EBDAA',
      colorPresetId: 'gold',
      businessHours: {
        saturday:  { open: '09:00', close: '22:00', closed: false },
        sunday:    { open: '09:00', close: '22:00', closed: false },
        monday:    { open: '09:00', close: '22:00', closed: false },
        tuesday:   { open: '09:00', close: '22:00', closed: false },
        wednesday: { open: '09:00', close: '22:00', closed: false },
        thursday:  { open: '09:00', close: '22:00', closed: false },
        friday:    { open: '14:00', close: '22:00', closed: false },
      },
    });
    setLogoPreview('');
    setSelectedColorId('gold');
    onToast('تم استعادة الإعدادات الافتراضية');
  };

  return (
    <div className="flex flex-col gap-6">

      {/* ── 1. Store Name ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-store-2-line text-white text-xs"></i>
          </div>
          اسم المتجر
        </h3>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">اسم المتجر</label>
          <input
            type="text"
            value={form.storeName}
            onChange={e => { setForm(f => ({ ...f, storeName: e.target.value })); setErrors(er => ({ ...er, storeName: '' })); }}
            placeholder="مثال: متجر الإبداع"
            className={`w-full border ${errors.storeName ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          />
          {errors.storeName && <p className="text-red-500 text-xs">{errors.storeName}</p>}
        </div>
        <SaveButton state={storeNameSave.state} onClick={handleSaveStoreName} label="حفظ اسم المتجر" />
      </div>

      {/* ── 2. Logo ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-image-line text-black text-xs"></i>
          </div>
          شعار المتجر (اللوجو)
        </h3>

        <div className="flex gap-2 mb-4 bg-white rounded-xl p-1 border border-gray-100">
          <button
            onClick={() => setLogoTab('image')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              logoTab === 'image' ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:text-[#1a1a1a]'
            }`}
          >
            <i className="ri-image-line ml-1"></i>رفع صورة
          </button>
          <button
            onClick={() => setLogoTab('text')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              logoTab === 'text' ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:text-[#1a1a1a]'
            }`}
          >
            <i className="ri-text ml-1"></i>نص اللوجو
          </button>
        </div>

        {logoTab === 'image' ? (
          <div className="flex flex-col gap-3">
            <div
              onClick={() => logoInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${
                logoPreview ? 'border-amber-400/50 bg-amber-50/30' : 'border-gray-200 hover:border-amber-400/40 hover:bg-amber-50/20'
              }`}
            >
              {logoPreview ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-32 h-16 flex items-center justify-center bg-[#0a0a0a] rounded-xl overflow-hidden">
                    <img src={logoPreview} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-xs text-amber-600 font-medium">انقر لتغيير الصورة</p>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl">
                    <i className="ri-upload-cloud-2-line text-amber-500 text-2xl"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-[#1a1a1a]">
                      {logoUploading ? 'جاري الرفع...' : 'انقر لرفع اللوجو'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG — حجم أقصى 2MB</p>
                    <p className="text-xs text-gray-400">يُفضّل خلفية شفافة (PNG)</p>
                  </div>
                </>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFileChange} />
            </div>
            {errors.logo && <p className="text-red-500 text-xs">{errors.logo}</p>}
            {logoPreview && (
              <button
                onClick={handleRemoveLogo}
                className="flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-medium text-red-500 border border-red-200 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line"></i>حذف الصورة والعودة للنص
              </button>
            )}
            {logoPreview && (
              <div className="bg-[#0a0a0a] rounded-xl p-4 flex items-center justify-center gap-3">
                <p className="text-gray-500 text-xs ml-3">معاينة على الـ Navbar:</p>
                <img src={logoPreview} alt="Navbar Logo" className="h-10 object-contain" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">اسم المتجر في اللوجو</label>
              <input
                type="text"
                value={form.logoText}
                onChange={e => setForm(f => ({ ...f, logoText: e.target.value }))}
                placeholder="مثال: إبداع"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">النص الثانوي (اختياري)</label>
              <input
                type="text"
                value={form.logoSubText}
                onChange={e => setForm(f => ({ ...f, logoSubText: e.target.value }))}
                placeholder="مثال: AL EBDAA"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
                dir="ltr"
              />
            </div>
            <div className="bg-[#0a0a0a] rounded-xl p-4 flex items-center justify-center">
              <p className="text-gray-500 text-xs ml-4">معاينة:</p>
              <div className="flex flex-col leading-none">
                <span
                  className="font-black text-lg tracking-wide"
                  style={{
                    fontFamily: "'Cairo', sans-serif",
                    background: 'linear-gradient(to left, #fcd34d, #fbbf24, #f59e0b)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {form.logoText || 'إبداع'}
                </span>
                {form.logoSubText && (
                  <span
                    className="text-[9px] tracking-[0.2em] uppercase font-semibold"
                    style={{ fontFamily: "'Cairo', sans-serif", color: 'rgba(251,191,36,0.4)' }}
                  >
                    {form.logoSubText}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <SaveButton state={logoSave.state} onClick={handleSaveLogo} label="حفظ الشعار" />
      </div>

      {/* ── 3. Colors ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: COLOR_PRESETS.find(p => p.id === selectedColorId)?.primary ?? '#fbbf24' }}>
            <i className="ri-palette-line text-xs" style={{ color: COLOR_PRESETS.find(p => p.id === selectedColorId)?.textOnPrimary ?? '#000' }}></i>
          </div>
          ألوان المتجر
        </h3>
        <p className="text-xs text-gray-400 mb-4">اختر اللون الرئيسي الذي يظهر في الأزرار والأسعار والتفاصيل</p>
        <div className="grid grid-cols-5 gap-3 mb-4">
          {COLOR_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => {
                setSelectedColorId(preset.id);
                setForm(f => ({ ...f, colorPresetId: preset.id }));
                const root = document.documentElement;
                root.style.setProperty('--color-primary', preset.primary);
                root.style.setProperty('--color-primary-light', preset.primaryLight);
                root.style.setProperty('--color-primary-dark', preset.primaryDark);
                root.style.setProperty('--color-text-on-primary', preset.textOnPrimary);
              }}
              title={preset.name}
              className="relative flex flex-col items-center gap-1.5 cursor-pointer group"
            >
              <div
                className="w-full aspect-square rounded-xl border-2 transition-all duration-200"
                style={{
                  background: `linear-gradient(135deg, ${preset.primaryLight} 0%, ${preset.primary} 50%, ${preset.primaryDark} 100%)`,
                  borderColor: selectedColorId === preset.id ? preset.primary : 'transparent',
                  boxShadow: selectedColorId === preset.id
                    ? `0 0 0 3px ${preset.primary}40, 0 4px 12px ${preset.primary}30`
                    : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: selectedColorId === preset.id ? 'scale(1.08)' : 'scale(1)',
                }}
              >
                {selectedColorId === preset.id && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-xl">
                    <i className="ri-check-line text-sm font-black" style={{ color: preset.textOnPrimary }}></i>
                  </div>
                )}
              </div>
              <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">{preset.name}</span>
            </button>
          ))}
        </div>
        {(() => {
          const preset = COLOR_PRESETS.find(p => p.id === selectedColorId) ?? COLOR_PRESETS[0];
          return (
            <div className="bg-[#0f0f0f] rounded-xl p-4 flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <p className="text-gray-400 text-[10px] mb-1">معاينة مباشرة</p>
                <p
                  className="text-xl font-black"
                  style={{
                    background: `linear-gradient(to left, ${preset.primaryLight}, ${preset.primary}, ${preset.primaryDark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: `drop-shadow(0 0 8px ${preset.primary}60)`,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {preset.name}
                </p>
                <p className="text-gray-500 text-xs">اللون الرئيسي للمتجر</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap" style={{ background: preset.primary, color: preset.textOnPrimary }}>
                  أضف للسلة
                </div>
                <div
                  className="text-sm font-black"
                  style={{
                    background: `linear-gradient(to left, ${preset.primaryLight}, ${preset.primary})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  ٢٤٩ ر.س
                </div>
              </div>
            </div>
          );
        })()}
        <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
          <i className="ri-information-line text-amber-500"></i>
          التغيير يُطبَّق فوراً على المتجر عند الحفظ
        </p>
        <SaveButton state={colorSave.state} onClick={handleSaveColor} label="حفظ اللون" />
      </div>

      {/* ── 4. WhatsApp ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-green-500 rounded-lg">
            <i className="ri-whatsapp-line text-white text-xs"></i>
          </div>
          رقم واتساب المسؤول
        </h3>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">رقم الواتساب (بدون + أو مسافات)</label>
          <div className="relative">
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🇸🇦</span>
            <input
              type="text"
              value={form.adminWhatsApp}
              onChange={e => { setForm(f => ({ ...f, adminWhatsApp: e.target.value.replace(/\D/g, '') })); setErrors(er => ({ ...er, adminWhatsApp: '' })); }}
              placeholder="966541138847"
              className={`w-full border ${errors.adminWhatsApp ? 'border-red-300' : 'border-gray-200'} rounded-xl pr-10 pl-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white`}
              dir="ltr"
            />
          </div>
          {errors.adminWhatsApp && <p className="text-red-500 text-xs">{errors.adminWhatsApp}</p>}
          <p className="text-gray-400 text-[11px]">مثال: 966541138847 (رمز الدولة + الرقم بدون صفر)</p>
        </div>
        <div className="mt-3 bg-green-50 rounded-xl px-4 py-3 border border-green-100 flex items-center gap-2">
          <i className="ri-whatsapp-line text-green-600 text-base flex-shrink-0"></i>
          <p className="text-green-700 text-xs">
            رسائل الطلبات ستُرسل إلى: <span className="font-black" dir="ltr">+{form.adminWhatsApp}</span>
          </p>
        </div>
        <SaveButton state={whatsappSave.state} onClick={handleSaveWhatsApp} label="حفظ رقم الواتساب" />
      </div>

      {/* ── 5. Payment Methods ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-bank-card-line text-black text-xs"></i>
          </div>
          طرق الدفع
        </h3>
        {errors.payment && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
            <p className="text-red-600 text-xs">{errors.payment}</p>
          </div>
        )}
        <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-green-600 text-base"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1a1a1a]">الدفع عند الاستلام</p>
              <p className="text-xs text-gray-400 mt-0.5">يدفع العميل نقداً عند استلام الطلب</p>
            </div>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, paymentCashEnabled: !f.paymentCashEnabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.paymentCashEnabled ? 'bg-green-500' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.paymentCashEnabled ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
        <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
              <i className="ri-bank-line text-amber-600 text-base"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1a1a1a]">التحويل البنكي</p>
              <p className="text-xs text-gray-400 mt-0.5">يحوّل العميل المبلغ قبل الشحن</p>
            </div>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, paymentBankEnabled: !f.paymentBankEnabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.paymentBankEnabled ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.paymentBankEnabled ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
        {form.paymentBankEnabled && (
          <div className="mt-4 flex flex-col gap-4 bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs font-bold text-gray-600 flex items-center gap-1.5">
              <i className="ri-information-line text-amber-500"></i>
              بيانات الحساب البنكي
            </p>
            {[
              { key: 'bankName', label: 'اسم البنك', placeholder: 'مثال: بنك الراجحي', dir: 'rtl' },
              { key: 'bankAccountName', label: 'اسم صاحب الحساب', placeholder: 'مثال: محمد أحمد العتيبي', dir: 'rtl' },
              { key: 'bankAccountNumber', label: 'رقم الحساب', placeholder: '1234567890', dir: 'ltr' },
              { key: 'bankIban', label: 'رقم الآيبان (IBAN)', placeholder: 'SA0000000000000000000000', dir: 'ltr' },
            ].map(({ key, label, placeholder, dir }) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-500">{label}</label>
                <input
                  type="text"
                  value={(form as Record<string, unknown>)[key] as string}
                  onChange={e => {
                    const val = key === 'bankIban' ? e.target.value.toUpperCase() : e.target.value;
                    setForm(f => ({ ...f, [key]: val }));
                    setErrors(er => ({ ...er, [key]: '' }));
                  }}
                  placeholder={placeholder}
                  className={`w-full border ${errors[key] ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors ${key === 'bankIban' ? 'font-mono' : ''}`}
                  dir={dir}
                  style={dir === 'rtl' ? { fontFamily: "'Cairo', sans-serif" } : {}}
                />
                {errors[key] && <p className="text-red-500 text-xs">{errors[key]}</p>}
              </div>
            ))}
            {(form.bankName || form.bankAccountName || form.bankAccountNumber || form.bankIban) && (
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 text-white">
                <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5"><i className="ri-eye-line"></i>معاينة بطاقة البنك</p>
                <div className="flex flex-col gap-2">
                  {form.bankName && <div className="flex items-center justify-between"><span className="text-gray-400 text-xs">البنك</span><span className="text-white text-xs font-bold">{form.bankName}</span></div>}
                  {form.bankAccountName && <div className="flex items-center justify-between"><span className="text-gray-400 text-xs">الاسم</span><span className="text-white text-xs font-bold">{form.bankAccountName}</span></div>}
                  {form.bankAccountNumber && <div className="flex items-center justify-between"><span className="text-gray-400 text-xs">رقم الحساب</span><span className="text-amber-400 text-xs font-mono font-bold" dir="ltr">{form.bankAccountNumber}</span></div>}
                  {form.bankIban && <div className="flex items-center justify-between"><span className="text-gray-400 text-xs">IBAN</span><span className="text-amber-400 text-xs font-mono font-bold" dir="ltr">{form.bankIban}</span></div>}
                </div>
              </div>
            )}
          </div>
        )}
        <SaveButton state={paymentSave.state} onClick={handleSavePayment} label="حفظ طرق الدفع" />
      </div>

      {/* ── 6. Announcement ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-megaphone-line text-black text-xs"></i>
          </div>
          الشريط الإعلاني
        </h3>
        <div className="flex items-center justify-between mb-4 bg-white rounded-xl px-4 py-3 border border-gray-100">
          <div>
            <p className="text-sm font-medium text-[#1a1a1a]">تفعيل الشريط الإعلاني</p>
            <p className="text-xs text-gray-400 mt-0.5">يظهر في أعلى الصفحة الرئيسية</p>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, announcementEnabled: !f.announcementEnabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.announcementEnabled ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.announcementEnabled ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">نص الإعلان الرئيسي</label>
          <input
            type="text"
            value={form.announcementText}
            onChange={e => setForm(f => ({ ...f, announcementText: e.target.value }))}
            placeholder="مثال: شحن مجاني لجميع الطلبات"
            maxLength={100}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          />
          <p className="text-gray-400 text-[11px]">{form.announcementText.length}/100 حرف</p>
        </div>
        <div className="mt-3 bg-amber-400 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2">
          <i className="ri-megaphone-line text-black text-sm"></i>
          <p className="text-black text-xs font-bold">{form.announcementText || 'نص الإعلان...'}</p>
        </div>
        <SaveButton state={announcementSave.state} onClick={handleSaveAnnouncement} label="حفظ الشريط الإعلاني" />
      </div>

      {/* ── 7. CallMeBot ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-green-500 rounded-lg">
            <i className="ri-key-2-line text-white text-xs"></i>
          </div>
          إعداد الإرسال التلقائي عبر واتساب
          {callMeBotKey ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full mr-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>مفعّل
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mr-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>غير مفعّل
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-400 mb-4">أدخل CallMeBot API Key لتفعيل إرسال رسائل واتساب التلقائية</p>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
          <p className="text-green-700 text-xs font-bold mb-2 flex items-center gap-1.5"><i className="ri-information-line"></i>كيف تحصل على API Key؟ (مجاني)</p>
          <ol className="text-green-700 text-xs space-y-1.5 list-decimal list-inside">
            <li>أرسل رسالة واتساب لـ <a href="https://wa.me/34644597826?text=I%20allow%20callmebot%20to%20send%20me%20messages" target="_blank" rel="noopener noreferrer" className="font-bold underline cursor-pointer" dir="ltr">+34 644 59 78 26</a></li>
            <li>اكتب بالضبط: <code className="bg-green-100 px-1.5 py-0.5 rounded text-[11px] font-mono">I allow callmebot to send me messages</code></li>
            <li>ستصلك رسالة تحتوي على API Key الخاص بك</li>
            <li>انسخ الـ Key وأدخله في الحقل أدناه</li>
          </ol>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
            <i className="ri-key-2-line text-amber-500"></i>CallMeBot API Key
          </label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={callMeBotKey}
              onChange={e => setCallMeBotKey(e.target.value)}
              placeholder="أدخل API Key من CallMeBot..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-400 transition-colors bg-white pl-10"
              dir="ltr"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className={`text-sm ${showApiKey ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
            </button>
          </div>
        </div>
        <SaveButton state={callmebotSave.state} onClick={handleSaveCallMeBot} label="حفظ API Key" />
      </div>

      {/* ── 8. Business Hours ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-time-line text-white text-xs"></i>
          </div>
          ساعات العمل
        </h3>
        <p className="text-xs text-gray-400 mb-5">تظهر في صفحة التواصل والفوتر لإعلام العملاء بأوقات الدوام</p>
        <div className="flex flex-col gap-2">
          {DAY_ORDER.map((day) => {
            const entry = form.businessHours?.[day] ?? { open: '09:00', close: '22:00', closed: false };
            const isFriday = day === 'friday';
            return (
              <div
                key={day}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                  entry.closed ? 'bg-gray-100 border-gray-200 opacity-70' : isFriday ? 'bg-amber-50/50 border-amber-100' : 'bg-white border-gray-100'
                }`}
              >
                <div className="w-20 flex-shrink-0">
                  <span className={`text-xs font-bold ${entry.closed ? 'text-gray-400' : isFriday ? 'text-amber-700' : 'text-[#1a1a1a]'}`}>
                    {DAY_LABELS[day]}
                  </span>
                  {isFriday && !entry.closed && <p className="text-[10px] text-amber-500 font-medium">يوم الجمعة</p>}
                </div>
                <button
                  onClick={() => updateDayHours(day, 'closed', !entry.closed)}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${entry.closed ? 'bg-red-400' : 'bg-green-400'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${entry.closed ? 'left-0.5' : 'left-4'}`} />
                </button>
                {entry.closed ? (
                  <span className="text-xs text-red-400 font-bold flex-1 text-center">مغلق</span>
                ) : (
                  <div className="flex items-center gap-2 flex-1 justify-center flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">من</span>
                      <input type="time" value={entry.open} onChange={e => updateDayHours(day, 'open', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#1a1a1a] transition-colors bg-white w-24 cursor-pointer" />
                    </div>
                    <span className="text-gray-300 text-xs">—</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">إلى</span>
                      <input type="time" value={entry.close} onChange={e => updateDayHours(day, 'close', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#1a1a1a] transition-colors bg-white w-24 cursor-pointer" />
                    </div>
                  </div>
                )}
                {!entry.closed && (
                  <span className="text-[10px] text-gray-400 flex-shrink-0 hidden sm:block whitespace-nowrap" dir="rtl">
                    {formatTime(entry.open)} – {formatTime(entry.close)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <button
            onClick={() => {
              const allOpen = DAY_ORDER.reduce((acc, day) => ({ ...acc, [day]: { ...(form.businessHours?.[day] ?? {}), closed: false } }), {} as BusinessHours);
              setForm(f => ({ ...f, businessHours: allOpen }));
            }}
            className="text-xs text-gray-500 hover:text-[#1a1a1a] border border-gray-200 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line ml-1"></i>فتح كل الأيام
          </button>
          <button
            onClick={() => {
              const withFridayClosed = { ...form.businessHours, friday: { ...form.businessHours.friday, closed: true } };
              setForm(f => ({ ...f, businessHours: withFridayClosed }));
            }}
            className="text-xs text-gray-500 hover:text-[#1a1a1a] border border-gray-200 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-moon-line ml-1"></i>إغلاق الجمعة
          </button>
          <button
            onClick={() => {
              const allSameTime = DAY_ORDER.reduce((acc, day) => ({ ...acc, [day]: { ...form.businessHours?.[day], open: '09:00', close: '22:00' } }), {} as BusinessHours);
              setForm(f => ({ ...f, businessHours: allSameTime }));
            }}
            className="text-xs text-gray-500 hover:text-[#1a1a1a] border border-gray-200 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-time-line ml-1"></i>9ص – 10م للكل
          </button>
        </div>
        <SaveButton state={hoursSave.state} onClick={handleSaveHours} label="حفظ ساعات العمل" />
      </div>

      {/* ── Reset All ── */}
      <div className="flex justify-end">
        <button
          onClick={handleReset}
          className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line ml-1"></i>
          استعادة الإعدادات الافتراضية
        </button>
      </div>
    </div>
  );
}
